import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Menu, X, ChevronDown } from "lucide-react";
import Logo from "./Logo";
import ThemeToggle from "./ThemeToggle";
import { Button } from "../ui/button";
import { useAuth } from "../../lib/auth";
import { PRODUCTS, SOLUTIONS, INDUSTRIES } from "../../lib/marketingContent";

function MegaCol({ title, items, base }) {
  return (
    <div>
      <h4 className="text-[11px] uppercase tracking-wider text-ink/40 mb-3 font-semibold">{title}</h4>
      <ul className="space-y-2">
        {items.map((it) => (
          <li key={it._to || it.slug}>
            <Link
              to={it._to || `${base}/${it.slug}`}
              className="block text-sm text-ink/80 hover:text-gold transition-colors"
              data-testid={`mega-${base.replace("/", "")}-${(it._to || it.slug).replace(/^\//, "").replace(/\//g, "-")}`}
            >
              <span className="block font-medium">{it.name}</span>
              {it.short && <span className="block text-xs text-ink/40">{it.short}</span>}
            </Link>
          </li>
        ))}
      </ul>
    </div>
  );
}

function MegaPanel({ open, onClose, kind }) {
  if (!open) return null;
  return (
    <div className="absolute left-0 right-0 top-full bg-panel border-t border-line shadow-lg z-50" onMouseLeave={onClose} data-testid={`mega-${kind}`}>
      <div className="mx-auto max-w-7xl px-6 py-8 grid grid-cols-1 md:grid-cols-3 gap-8">
        {kind === "platform" && (
          <>
            <div className="md:col-span-3 -mt-3 pb-5 border-b border-line">
              <Link to="/platform" onClick={onClose} className="block group" data-testid="mega-platform-overview">
                <div className="flex items-center gap-2">
                  <span className="text-[10px] uppercase tracking-[0.3em] text-gold">ArgusSecure Platform</span>
                </div>
                <h3 className="font-display text-2xl mt-1 group-hover:text-gold transition-colors">
                  One platform. SAST · DAST · SCA · AI Fix-It → <span className="text-gold">View overview →</span>
                </h3>
                <p className="text-sm text-ink/60 mt-1">Take action on application risk with the integrated exposure-management platform.</p>
              </Link>
            </div>
            <MegaCol title="Scan" items={[PRODUCTS.sast, PRODUCTS.dast, PRODUCTS.sca]} base="/products" />
            <MegaCol title="Manage" items={[PRODUCTS.asm, PRODUCTS.remediation]} base="/products" />
            <MegaCol title="Prove" items={[PRODUCTS.compliance]} base="/products" />
          </>
        )}
        {kind === "solutions" && (
          <>
            <MegaCol title="By Use Case" items={[SOLUTIONS["application-security"], SOLUTIONS["devsecops"], SOLUTIONS["exposure-management"]]} base="/solutions" />
            <MegaCol title="By Need" items={[SOLUTIONS["compliance"], SOLUTIONS["cloud-security"], SOLUTIONS["ai-security"]]} base="/solutions" />
            <MegaCol title="By Industry" items={Object.values(INDUSTRIES).slice(0, 6)} base="/industries" />
          </>
        )}
        {kind === "resources" && (
          <>
            <MegaCol title="Learn" items={[
              { slug: "library", name: "Resource Library", short: "Whitepapers, briefs, guides" },
              { slug: "research", name: "Research Center", short: "What we found this month" },
              { slug: "blog", name: "Blog", short: "Notes from the floor" },
            ]} base="/resources" />
            <MegaCol title="Trust" items={[
              { slug: "library", name: "Customer Stories", short: "Real teams, real fixes" },
              { slug: "library", name: "Status", short: "Live system health" },
            ]} base="/resources" />
            <MegaCol title="Reference" items={[
              { slug: "library", name: "API Docs", short: "Build on the platform" },
              { slug: "library", name: "Glossary", short: "CVE, CWE, OWASP, plain English" },
              { slug: "", name: "Tool Landscape", short: "Where ArgusSecure fits in the security stack", _to: "/landscape" },
              { slug: "", name: "Customer Stories", short: "Illustrative scenarios + day-in-the-life", _to: "/customers" },
            ]} base="/resources" />
          </>
        )}
      </div>
    </div>
  );
}

export default function MarketingHeader() {
  const [open, setOpen] = useState(false);
  const [mega, setMega] = useState(null); // 'platform' | 'solutions' | 'resources' | null
  const { user } = useAuth();
  const nav = useNavigate();

  const handleEnter = (kind) => () => setMega(kind);
  const handleLeave = () => setMega(null);

  return (
    <header className="sticky top-0 z-40 bg-paper/90 backdrop-blur border-b border-line" data-testid="marketing-header" onMouseLeave={handleLeave}>
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
        <Link to="/" className="flex items-center" data-testid="header-logo-link">
          <Logo size={24} />
        </Link>
        <nav className="hidden md:flex items-center gap-1 ml-8">
          {[
            { key: "platform", label: "Platform" },
            { key: "solutions", label: "Solutions" },
          ].map((n) => (
            <button
              key={n.key}
              onMouseEnter={handleEnter(n.key)}
              onFocus={handleEnter(n.key)}
              className={`inline-flex items-center gap-1 px-3 h-10 text-sm transition-colors ${
                mega === n.key ? "text-gold" : "text-ink/80 hover:text-ink"
              }`}
              data-testid={`nav-${n.key}`}
            >
              {n.label} <ChevronDown size={12} />
            </button>
          ))}
          <Link to="/why-argus" className="px-3 h-10 inline-flex items-center text-sm text-ink/80 hover:text-ink" data-testid="nav-why">Why ArgusSecure</Link>
          <Link to="/pricing" className="px-3 h-10 inline-flex items-center text-sm text-ink/80 hover:text-ink" data-testid="nav-pricing">Pricing</Link>
          <button
            onMouseEnter={handleEnter("resources")}
            onFocus={handleEnter("resources")}
            className={`inline-flex items-center gap-1 px-3 h-10 text-sm transition-colors ${
              mega === "resources" ? "text-gold" : "text-ink/80 hover:text-ink"
            }`}
            data-testid="nav-resources"
          >
            Resources <ChevronDown size={12} />
          </button>
          <Link to="/about" className="px-3 h-10 inline-flex items-center text-sm text-ink/80 hover:text-ink" data-testid="nav-about">Company</Link>
        </nav>
        <div className="hidden md:flex items-center gap-2">
          <ThemeToggle />
          {user ? (
            <Button onClick={() => nav("/app")} className="bg-gold text-paper hover:bg-gold2 h-10 px-4" data-testid="header-open-app">
              Open Dashboard →
            </Button>
          ) : (
            <>
              <Link to="/login" className="text-sm text-ink hover:text-gold transition-colors px-3" data-testid="header-sign-in">
                Sign In →
              </Link>
              <Button onClick={() => nav("/signup")} className="bg-gold text-paper hover:bg-gold2 h-10 px-4 font-semibold" data-testid="header-start-scan">
                Start My Free Scan
              </Button>
            </>
          )}
        </div>
        <button className="md:hidden p-2 text-ink" onClick={() => setOpen((o) => !o)} aria-label="Menu" data-testid="mobile-menu-toggle">
          {open ? <X size={22} /> : <Menu size={22} />}
        </button>
      </div>
      <MegaPanel open={!!mega} onClose={handleLeave} kind={mega} />
      {open && (
        <div className="md:hidden border-t border-line bg-panel" data-testid="mobile-menu">
          <div className="px-4 py-4 flex flex-col gap-3">
            <Link to="/why-argus" className="text-base text-ink/80 py-1" onClick={() => setOpen(false)}>Why ArgusSecure</Link>
            <Link to="/pricing" className="text-base text-ink/80 py-1" onClick={() => setOpen(false)}>Pricing</Link>
            <Link to="/resources/library" className="text-base text-ink/80 py-1" onClick={() => setOpen(false)}>Resources</Link>
            <Link to="/about" className="text-base text-ink/80 py-1" onClick={() => setOpen(false)}>Company</Link>
            <div className="text-xs uppercase tracking-wider text-ink/40 mt-3">Platform</div>
            {Object.values(PRODUCTS).map((p) => (
              <Link key={p.slug} to={`/products/${p.slug}`} className="text-sm text-ink/70 pl-2" onClick={() => setOpen(false)}>
                {p.name}
              </Link>
            ))}
            <div className="flex items-center gap-2 pt-3 mt-3 border-t border-line">
              <ThemeToggle />
              {user ? (
                <Link to="/app" className="flex-1 inline-flex items-center justify-center bg-gold text-paper rounded-md px-4 h-12 font-semibold" onClick={() => setOpen(false)} data-testid="mobile-open-app">
                  Open Dashboard →
                </Link>
              ) : (
                <>
                  <Link to="/login" className="flex-1 inline-flex items-center justify-center border border-line text-ink rounded-md px-4 h-12 font-semibold" onClick={() => setOpen(false)} data-testid="mobile-sign-in">
                    Sign In
                  </Link>
                  <Link to="/signup" className="flex-1 inline-flex items-center justify-center bg-gold text-paper rounded-md px-4 h-12 font-semibold" onClick={() => setOpen(false)} data-testid="mobile-start-scan">
                    Free Scan
                  </Link>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </header>
  );
}
